import { thermostatsCollection } from '@/reducers/thermostats'
import { utilityPlanRateIntervalsCollection } from '@/reducers/utilityPlanRateIntervalsCollection'
import { fetchAuthenticated } from '@/utils/fetch'
import { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { debounce } from 'lodash'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { ThermostatSettings } from '@/types'
import { useAppDispatch } from '@/hooks'
import { useNavigation } from '@/app/hooks'
import useCostCategories from '@/hooks/useCostCategories'
import {
  useThermostatInactivityPeriodsCollection,
  transformFromApiFormat,
} from '@/reducers/thermostatInactivityPeriods'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { inactiveComfortSettingsCollection } from '@/reducers/inactiveComfortSettings'
import { getInactivePeriodSummaryLines } from './inactive-times/utils'
import { match } from 'ts-pattern'
import useCostCategoriesToggleStatus from './useCostCategoriesToggleStatus'
import useThermostatStatusModes from './useThermostatStatusModes'
import useDeviceEnrollments from '@/hooks/useDeviceEnrollments/useDeviceEnrollments'
import { getInactivePeriodSummarySubtitleLines } from './utils/inactivityPeriodFormatting'
import useCostCategoryBarGraphViewModel from './useCostCategoryBarGraphViewModel'
import { thermostatCostHistorySummaryCollection } from '@/reducers/thermostatCostHistorySummaryCollection'
import { thermostatSavingsSummariesCollection } from '@/reducers/thermostatSavingsSummaries'
import { ThermostatSavingsSummary } from '@/types'

export default function useViewModel() {
  const { thermostatId: thermostatIdParam } = useParams<{
    thermostatId: string
  }>()
  const thermostatId = Number.parseInt(thermostatIdParam)
  const { data: thermostats, isLoading: thermostatsLoading } =
    thermostatsCollection.useFetch()
  const { deviceEnrollments, isLoading: enrollmentLoading } =
    useDeviceEnrollments('thermostat', thermostatId)

  // Fetch cost history summary for month savings
  const { data: costHistoryList, isLoadingOrIdle: costHistoryLoading } =
    thermostatCostHistorySummaryCollection.useFetch({
      path: `thermostats/${thermostatId}/cost_history/summary`,
    })
  const costHistory = costHistoryList[0]

  // Fetch savings summary for annual savings
  const { data: savingsSummaryList, isLoading: savingsSummaryLoading } =
    thermostatSavingsSummariesCollection.useFetch(
      `/thermostats/${thermostatId}/savings_summary/`,
    )
  const savingsSummary = savingsSummaryList[0] as
    | ThermostatSavingsSummary
    | undefined
  const dispatch = useAppDispatch()
  const updateQueue = useRef<Array<{ categoryIndex: number; value: number }>>(
    [],
  )
  // If not undefined, this will force the toggle to be permanently in
  // either an on or off state to reflect the true backend behavior of
  // the enrollment group behavior
  let forcedCostCategoryControlState = null
  if (deviceEnrollments) {
    for (const enrollment of deviceEnrollments) {
      if (
        enrollment?.enrollment?.enrolled === true &&
        enrollment?.enrollment?.group?.cost_category_optimization_enabled !=
          null
      ) {
        forcedCostCategoryControlState =
          enrollment?.enrollment?.group?.cost_category_optimization_enabled
        break
      }
    }
  }

  const isProcessing = useRef(false)
  const debouncedFunctions = useRef<
    Record<number, ReturnType<typeof debounce>>
  >({})

  const {
    costCategories,
    isLoading: costCategoryControlsLoading,
    refetch: refetchCostCategoryControls,
  } = useCostCategories(thermostatId)
  const { data: planIntervals, isLoading: utilityPlanRateIntervalsLoading } =
    utilityPlanRateIntervalsCollection.useFetch()
  const thermostat = useMemo(
    () => thermostats.find((t) => t.id === thermostatId),
    [thermostats],
  )

  const { data: settingsArray, isLoading: thermostatSettingsLoading } =
    thermostatSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/settings/`,
    )
  const thermostatSettings = settingsArray[0] as ThermostatSettings | undefined

  const isInInactivePeriod = Boolean(
    thermostat?.is_in_inactive_period_or_comfort_setting,
  )
  const isToggleDisabled = forcedCostCategoryControlState != null

  const costCategoryOptimizationEnabledMessage = match(isInInactivePeriod)
    .with(false, () => {
      if (deviceEnrollments) {
        for (const enrollment of deviceEnrollments) {
          const message =
            enrollment?.enrollment?.view_config_json?.thermostatSaveMoneyAlert
          if (message) {
            return message
          }
        }
      }
      return null
    })
    .otherwise(() => null)

  const intervals = useMemo(
    () =>
      planIntervals.toSorted(
        (a, b) =>
          new Date(a.start_datetime).getTime() -
          new Date(b.start_datetime).getTime(),
      ),
    [planIntervals],
  )

  const processQueue = useCallback(async () => {
    if (isProcessing.current) return

    isProcessing.current = true
    const nextItem = updateQueue.current.shift()
    if (!nextItem) {
      isProcessing.current = false
      return
    }
    const { categoryIndex, value } = nextItem

    try {
      const response = await fetchAuthenticated(
        `/thermostats/${thermostatId}/cost_category_controls`,
        { method: 'GET' },
      )
      const updatedCostControls =
        (await response.json()) as typeof costCategories

      const category = updatedCostControls[categoryIndex]
      if (!category) return

      const categories = [
        {
          ...category,
          cool_setpoint_delta: value,
        },
        ...updatedCostControls.filter((_, i) => i !== categoryIndex),
      ]

      await fetchAuthenticated(
        `/thermostats/${thermostatId}/cost_category_controls`,
        {
          body: JSON.stringify(categories),
          method: 'PATCH',
        },
      )

      dispatch(refetchCostCategoryControls)
    } finally {
      isProcessing.current = false
      if (updateQueue.current.length > 0) {
        processQueue()
      }
    }
  }, [thermostatId, refetchCostCategoryControls, dispatch])

  const onCostCategoryInputChange = useCallback(
    (categoryIndex: number, value: number) => {
      if (!debouncedFunctions.current[categoryIndex]) {
        debouncedFunctions.current[categoryIndex] = debounce(
          (value: number) => {
            updateQueue.current = updateQueue.current.filter(
              (item) => item.categoryIndex !== categoryIndex,
            )
            updateQueue.current.push({ categoryIndex, value })
            processQueue()
          },
          800,
        )
      }
      debouncedFunctions.current[categoryIndex](value)
    },
    [processQueue],
  )

  const {
    saveMoneyEnabled,
    onToggleChange,
    allCostCategoriesSetToZero,
    isZeroCategoriesModalOpen,
    openZeroCategoriesModal,
    closeZeroCategoriesModal,
    isActiveDREvent,
    activeDrEvent,
    isProgramOverride,
  } = useCostCategoriesToggleStatus({
    thermostatId,
    thermostatSettings,
    forcedCostCategoryControlState,
    isInInactivePeriod,
    isToggleDisabled,
    costCategories,
  })

  const statusModeCalculation = useThermostatStatusModes({
    thermostatId,
    isInInactivePeriod,
    allCostCategoriesSetToZero,
    saveMoneyEnabled,
    forcedCostCategoryControlState,
    isDREventActive: isActiveDREvent,
  })

  const lowCategoryIndex = costCategories.findIndex(
    (category) => category.minValue === 0,
  )

  // Calculate month savings from cost history
  const monthCostSummary = costHistory?.month
  const monthCostOfOptiwattAdjustmentsCents =
    monthCostSummary?.cost_of_optiwatt_adjustments_cents || 0

  // Get annual savings from savings summary
  const annualSavingsCents = savingsSummary?.cost_category_savings_cents || 0

  // Separate loading state for savings data (doesn't block page)
  const savingsDataLoading = costHistoryLoading || savingsSummaryLoading

  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    // Exclude savings data loading from page-level loading
    const loading =
      thermostatSettingsLoading ||
      costCategoryControlsLoading ||
      thermostatsLoading ||
      utilityPlanRateIntervalsLoading ||
      enrollmentLoading
    if (!loading) {
      setIsLoading(loading)
    }
  }, [
    thermostatSettingsLoading,
    costCategoryControlsLoading,
    thermostatsLoading,
    utilityPlanRateIntervalsLoading,
    enrollmentLoading,
  ])

  const navigate = useNavigation()
  const onEditCostCategoriesClick = () => {
    navigate.push(`/devices/thermostat/${thermostatId}/cost-categories/edit`)
  }
  // Modal controls for inactive times explanation
  const [isInactiveTimesModalOpen, setIsInactiveTimesModalOpen] =
    useState(false)
  const openInactiveTimesModal = () => setIsInactiveTimesModalOpen(true)
  const closeInactiveTimesModal = () => setIsInactiveTimesModalOpen(false)

  // Inactive time summary and routing information
  const inactivityPeriods =
    useThermostatInactivityPeriodsCollection(thermostatId)
  const periodSummaryLines = useMemo(() => {
    try {
      const ui = inactivityPeriods.data.map(transformFromApiFormat)
      return getInactivePeriodSummaryLines(ui)
    } catch {
      return []
    }
  }, [inactivityPeriods.data])

  const { data: comfortSettings } =
    thermostatComfortSettingsCollection.useFetch(
      `/thermostats/${thermostatId}/comfort_settings/`,
    )
  const firstComfortSettingId = comfortSettings[0]?.id
  const inactiveSchedulesFetch = inactiveComfortSettingsCollection.useFetch(
    `/thermostats/${thermostatId}/comfort_settings/${
      firstComfortSettingId ?? ''
    }/inactive/`,
    { require: Boolean(firstComfortSettingId) },
  )

  const hasInactiveTimesConfigured = useMemo(() => {
    const hasPeriods = periodSummaryLines.length > 0
    const hasSchedules = inactiveSchedulesFetch.data.length > 0
    return hasPeriods || hasSchedules
  }, [periodSummaryLines.length, inactiveSchedulesFetch.data.length])

  const shouldShowInactiveTimesCard =
    hasInactiveTimesConfigured || saveMoneyEnabled || allCostCategoriesSetToZero

  const inactiveTimesCardSubtitleLines = !thermostat
    ? []
    : getInactivePeriodSummarySubtitleLines({
        thermostat,
        inactivityPeriods: inactivityPeriods.data,
        comfortSettings,
        inactiveComfortSettings: inactiveSchedulesFetch.data,
      })

  const onInactiveTimesCardClick = () => {
    const type = thermostat?.inactivity_type
    if (type === 'schedule') {
      navigate.push(
        `/devices/thermostat/${thermostatId}/cost-categories/inactive-times/schedule`,
      )
      return
    }
    navigate.push(
      `/devices/thermostat/${thermostatId}/cost-categories/inactive-times/periods`,
    )
  }

  // Unified function to open the appropriate modal based on current state
  const openSaveMoneyIssueModal = useCallback(() => {
    if (isProgramOverride) {
      openZeroCategoriesModal() // Use the same modal, it will handle program case
      return
    }
    if (isActiveDREvent) {
      openZeroCategoriesModal() // Use the same modal, it will handle DR event case
      return
    }
    if (isInInactivePeriod) {
      openInactiveTimesModal()
    }
    if (allCostCategoriesSetToZero) {
      openZeroCategoriesModal()
    }
  }, [
    isProgramOverride,
    isActiveDREvent,
    isInInactivePeriod,
    allCostCategoriesSetToZero,
    openInactiveTimesModal,
    openZeroCategoriesModal,
  ])

  const costCategoryBarGraph = useCostCategoryBarGraphViewModel(
    intervals,
    costCategories,
  )

  return {
    thermostat,
    thermostatSettings,
    intervals,
    costCategories,
    costCategoryBarGraph,
    onCostCategoryInputChange,
    saveMoneyEnabled,
    onToggleChange,
    isToggleDisabled,
    isInactive: isInInactivePeriod,
    lowCategoryIndex,
    isLoading,
    costCategoryOptimizationEnabledMessage,
    onEditCostCategoriesClick,
    shouldShowInactiveTimesCard,
    inactiveTimesCardSubtitleLines,
    onInactiveTimesCardClick,
    isInactiveTimesModalOpen,
    openInactiveTimesModal,
    closeInactiveTimesModal,
    allCostCategoriesSetToZero,
    isZeroCategoriesModalOpen,
    openZeroCategoriesModal,
    closeZeroCategoriesModal,
    openSaveMoneyIssueModal,
    thermostatStatus: statusModeCalculation.thermostatStatus,
    isActiveDREvent,
    activeDrEvent,
    isProgramOverride,
    monthCostOfOptiwattAdjustmentsCents,
    annualSavingsCents,
    thermostatId,
    savingsDataLoading,
  }
}
